import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { X, Share2, Check, Phone, Mail, Sparkles, Star, ChevronLeft, ChevronRight, ZoomIn, Facebook } from 'lucide-react';
import { IdCard } from 'lucide-react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { API, photoUrl } from '../lib/api';

const PublicCatalogue = () => {
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const [selectedImage, setSelectedImage] = useState(0);
  const [copied, setCopied] = useState(false);
  const [copiedVehicle, setCopiedVehicle] = useState(false);
  const [typeFilter, setTypeFilter] = useState('tous'); // tous, voiture, utilitaire
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const touchStartX = React.useRef(null);

  useEffect(() => {
    fetchVehicles();
  }, []);

  // Ouvrir automatiquement la fiche véhicule si l'URL contient ?v={id}
  useEffect(() => {
    if (vehicles.length === 0) return;
    const params = new URLSearchParams(window.location.search);
    const vId = params.get('v');
    if (vId) {
      const found = vehicles.find(v => v.id === vId);
      if (found) {
        openVehicleModal(found);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vehicles]);

  const fetchVehicles = async () => {
    setLoadError(false);
    try {
      const res = await axios.get(`${API}/public/vehicles`, { timeout: 20000 });
      // Convert relative /api/public/photos/... URLs to absolute
      const data = (res.data || []).map(v => ({
        ...v,
        photos: (v.photos || []).map(photoUrl),
      }));
      setVehicles(data);
    } catch (error) {
      console.error('Error fetching vehicles:', error);
      setLoadError(true);
      toast.error('Erreur lors du chargement des véhicules');
    } finally {
      setLoading(false);
    }
  };

  const openVehicleModal = async (vehicle) => {
    setSelectedImage(0);
    setSelectedVehicle(vehicle); // immediate open with thumbnail
    // Load full-resolution photos in the background via the dedicated endpoint
    if ((vehicle.photos_count || 0) >= 1) {
      try {
        const res = await axios.get(`${API}/public/vehicles/${vehicle.id}/photos`, { timeout: 20000 });
        const fullPhotos = (res.data?.photos || []).map(photoUrl);
        if (fullPhotos.length > 0) {
          setSelectedVehicle(prev => (prev && prev.id === vehicle.id ? { ...prev, photos: fullPhotos } : prev));
        }
      } catch (err) {
        console.error('Failed to load full photos:', err);
      }
    }
    // Increment view counter
    axios.post(`${API}/vehicles/${vehicle.id}/view`).catch(err =>
      console.error('Failed to increment view:', err)
    );
  };

  const copyLink = () => {
    // Toujours utiliser le domaine public officiel pour les liens partagés
    const base = process.env.REACT_APP_PUBLIC_URL || window.location.origin;
    const url = `${base}/catalogue`;
    navigator.clipboard.writeText(url);
    setCopied(true);
    toast.success('Lien copié dans le presse-papier !');
    setTimeout(() => setCopied(false), 2000);
  };

  const copyVehicleLink = (vehicleId) => {
    const base = process.env.REACT_APP_PUBLIC_URL || window.location.origin;
    const url = `${base}/catalogue?v=${vehicleId}`;
    navigator.clipboard.writeText(url);
    setCopiedVehicle(true);
    toast.success('Lien de la fiche copié !');
    setTimeout(() => setCopiedVehicle(false), 2000);
  };

  const shareVehicleNative = async (vehicle) => {
    const base = process.env.REACT_APP_PUBLIC_URL || window.location.origin;
    const url = `${base}/catalogue?v=${vehicle.id}`;
    const title = `${vehicle.marque} ${vehicle.modele}`;
    const text = `Découvrez ${vehicle.marque} ${vehicle.modele} (${vehicle.annee}) - Groupe Auto Prestige`;
    if (navigator.share) {
      try {
        await navigator.share({ title, text, url });
        return;
      } catch (err) {
        // L'utilisateur a annulé : silencieux
        if (err && err.name === 'AbortError') return;
      }
    }
    // Fallback : copier dans le presse-papier
    copyVehicleLink(vehicle.id);
  };

  const photos = selectedVehicle?.photos || [];
  const totalPhotos = photos.length;

  const prevPhoto = React.useCallback(() => {
    if (totalPhotos < 2) return;
    setSelectedImage(prev => (prev - 1 + totalPhotos) % totalPhotos);
  }, [totalPhotos]);

  const nextPhoto = React.useCallback(() => {
    if (totalPhotos < 2) return;
    setSelectedImage(prev => (prev + 1) % totalPhotos);
  }, [totalPhotos]);

  // Navigation clavier dans la lightbox
  useEffect(() => {
    if (!lightboxOpen) return;
    const handler = (e) => {
      if (e.key === 'Escape') setLightboxOpen(false);
      else if (e.key === 'ArrowLeft') prevPhoto();
      else if (e.key === 'ArrowRight') nextPhoto();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [lightboxOpen, prevPhoto, nextPhoto]);

  const handleTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX;
  };

  const handleTouchEnd = (e) => {
    if (touchStartX.current === null) return;
    const dx = e.changedTouches[0].clientX - touchStartX.current;
    if (Math.abs(dx) > 50) {
      if (dx > 0) prevPhoto();
      else nextPhoto();
    }
    touchStartX.current = null;
  };

  const closeModal = () => {
    setSelectedVehicle(null);
    setSelectedImage(0);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-slate-900 mx-auto mb-4"></div>
          <p className="text-slate-600">Chargement du catalogue...</p>
        </div>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <div className="text-center max-w-md" data-testid="load-error-view">
          <div className="text-5xl mb-4">🚗💨</div>
          <h2 className="text-xl font-bold text-slate-900 mb-2" style={{fontFamily: 'Manrope'}}>
            Impossible de charger le catalogue
          </h2>
          <p className="text-slate-600 mb-6">
            Vérifiez votre connexion internet ou réessayez dans quelques instants.
          </p>
          <button
            onClick={() => { setLoading(true); fetchVehicles(); }}
            data-testid="retry-load-btn"
            className="px-6 py-3 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors font-semibold"
          >
            Réessayer
          </button>
        </div>
      </div>
    );
  }

  const voitures = vehicles.filter(v => v.type_vehicule === 'voiture' || !v.type_vehicule);
  const utilitaires = vehicles.filter(v => v.type_vehicule === 'utilitaire');

  // Appliquer le filtre de type
  const filteredVehicles = typeFilter === 'tous' 
    ? vehicles 
    : vehicles.filter(v => 
        typeFilter === 'voiture' 
          ? (v.type_vehicule === 'voiture' || !v.type_vehicule)
          : v.type_vehicule === 'utilitaire'
      );

  const displayedVoitures = typeFilter === 'tous' || typeFilter === 'voiture' ? voitures : [];
  const displayedUtilitaires = typeFilter === 'tous' || typeFilter === 'utilitaire' ? utilitaires : [];

  const renderVehicleCard = (vehicle) => (
    <div
      key={vehicle.id}
      data-testid={`public-vehicle-card-${vehicle.id}`}
      onClick={() => openVehicleModal(vehicle)}
      className="bg-white rounded-xl shadow-md hover:shadow-xl transition-all duration-300 overflow-hidden cursor-pointer transform hover:-translate-y-1"
    >
      <div className="relative h-56 bg-slate-200">
        {vehicle.photos && vehicle.photos[0] ? (
          <img
            src={vehicle.photos[0]}
            alt={`${vehicle.marque} ${vehicle.modele}`}
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-slate-400">
            <span>Pas de photo</span>
          </div>
        )}
        <div className="absolute top-3 right-3">
          <span className="bg-green-500 text-white text-xs font-semibold px-3 py-1 rounded-full shadow-lg">
            Disponible
          </span>
        </div>
      </div>
      
      <div className="p-6">
        <h3 className="text-xl font-bold text-slate-900 mb-1" style={{fontFamily: 'Manrope'}}>
          {vehicle.marque} {vehicle.modele}
        </h3>
        <p className="text-sm text-slate-600 font-mono mb-4">
          {vehicle.type_vehicule === 'utilitaire' ? 'Utilitaire' : 'Voiture'} · {vehicle.annee}
        </p>
        
        <div className="space-y-2 mb-4">
          <div className="flex justify-between text-sm">
            <span className="text-slate-600">Année</span>
            <span className="font-semibold text-slate-900">{vehicle.annee}</span>
          </div>
          {vehicle.kilometrage && (
            <div className="flex justify-between text-sm">
              <span className="text-slate-600">Kilométrage</span>
              <span className="font-mono text-slate-900">{vehicle.kilometrage.toLocaleString('fr-FR')} km</span>
            </div>
          )}
          {vehicle.couleur && (
            <div className="flex justify-between text-sm">
              <span className="text-slate-600">Couleur</span>
              <span className="text-slate-900">{vehicle.couleur}</span>
            </div>
          )}
        </div>
        
        {vehicle.prix_vente && (
          <div className="pt-4 border-t border-slate-200">
            <div className="flex justify-between items-center">
              <span className="text-slate-600 text-sm">Prix TTC</span>
              <span className="text-2xl font-bold text-green-600 font-mono">
                {vehicle.prix_vente.toLocaleString('fr-FR')} €
              </span>
            </div>
            {vehicle.tva_applicable && (
              <div className="flex justify-end mt-1">
                <span data-testid={`prix-ht-${vehicle.id}`} className="text-xs text-slate-500 font-mono">
                  soit {Math.round(vehicle.prix_vente / 1.20).toLocaleString('fr-FR')} € HT
                </span>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      {/* Widget flottant réseaux sociaux — visible pendant tout le scroll */}
      <div
        data-testid="social-floating-widget"
        className="fixed right-4 top-1/2 -translate-y-1/2 z-30 flex flex-col items-center gap-3 px-3 py-4 rounded-2xl bg-white/90 backdrop-blur-md shadow-2xl border border-red-200 hover:border-red-400 transition-all"
      >
        <Link
          to="/carte"
          data-testid="floating-carte-visite"
          aria-label="Notre carte de visite"
          title="Notre carte de visite"
          className="flex items-center justify-center w-10 h-10 rounded-full bg-red-600 hover:bg-red-700 text-white transition-all hover:scale-110 shadow-lg"
        >
          <IdCard size={18} />
        </Link>
        <a
          href="https://www.facebook.com/share/1FEJfG9TN1/"
          target="_blank"
          rel="noopener noreferrer"
          data-testid="social-facebook"
          aria-label="Facebook"
          title="Suivez-nous sur Facebook"
          className="flex items-center justify-center w-10 h-10 rounded-full bg-blue-600 hover:bg-blue-700 text-white transition-all hover:scale-110 shadow-lg"
        >
          <Facebook size={18} className="fill-current" />
        </a>
        <a
          href="https://www.tiktok.com/@gap.ou.pas.gap"
          target="_blank"
          rel="noopener noreferrer"
          data-testid="social-tiktok"
          aria-label="TikTok"
          title="Suivez-nous sur TikTok"
          className="flex items-center justify-center w-10 h-10 rounded-full bg-black hover:bg-slate-800 text-white transition-all hover:scale-110 shadow-lg"
        >
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="18" height="18" fill="currentColor" aria-hidden="true">
            <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5.8 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1.84-.1z" />
          </svg>
        </a>
      </div>

      {/* Header */}
      <header className="bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 border-b-2 border-red-600 shadow-xl sticky top-0 z-40 relative overflow-hidden">
        {/* Effet de lumière subtil rouge */}
        <div className="pointer-events-none absolute -top-24 -right-24 w-64 h-64 bg-red-600/8 rounded-full blur-3xl" />
        <div className="pointer-events-none absolute -bottom-24 -left-24 w-64 h-64 bg-red-600/8 rounded-full blur-3xl" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-2 relative">
          {/* Logo à gauche + bouton Partager à droite */}
          <div className="flex items-center justify-between gap-3 mb-3">
            <div className="flex items-center gap-4">
              <img 
                src="https://customer-assets.emergentagent.com/job_auto-flip-tracker-1/artifacts/mote16qp_26609.jpg" 
                alt="Groupe Auto Prestige" 
                className="h-24 sm:h-28 w-auto object-contain drop-shadow-[0_0_20px_rgba(220,38,38,0.5)]"
                style={{ mixBlendMode: 'screen', filter: 'contrast(1.15) brightness(1.15)' }}
              />
              <div className="tagline-wrapper relative inline-flex items-center px-4">
                {/* Étoiles décoratives autour de la phrase */}
                <Sparkles
                  size={16}
                  className="tagline-star absolute -top-2 -left-1"
                  style={{ animationDelay: '0.9s, 1.4s' }}
                  aria-hidden="true"
                />
                <Star
                  size={12}
                  fill="currentColor"
                  className="tagline-star absolute -top-3 left-1/3"
                  style={{ animationDelay: '1.1s, 1.9s' }}
                  aria-hidden="true"
                />
                <Sparkles
                  size={14}
                  className="tagline-star absolute -bottom-2 left-1/4"
                  style={{ animationDelay: '1.3s, 2.4s' }}
                  aria-hidden="true"
                />
                <Star
                  size={10}
                  fill="currentColor"
                  className="tagline-star absolute top-0 right-2"
                  style={{ animationDelay: '1.0s, 1.6s' }}
                  aria-hidden="true"
                />
                <Sparkles
                  size={18}
                  className="tagline-star absolute -top-1 -right-3"
                  style={{ animationDelay: '1.2s, 2.1s' }}
                  aria-hidden="true"
                />
                <Star
                  size={12}
                  fill="currentColor"
                  className="tagline-star absolute -bottom-3 right-1/3"
                  style={{ animationDelay: '1.4s, 2.6s' }}
                  aria-hidden="true"
                />
                <p
                  data-testid="tagline-floating"
                  className="tagline-floating text-sm sm:text-base md:text-lg font-semibold italic tracking-wide whitespace-nowrap"
                  style={{ fontFamily: 'Manrope' }}
                >
                  Votre véhicule à juste prix
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={copyLink}
                data-testid="copy-link-btn"
                title="Partager le lien"
                aria-label="Partager"
                className="flex items-center justify-center p-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-all shadow"
              >
                {copied ? <Check size={18} /> : <Share2 size={18} />}
              </button>
            </div>
          </div>

          {/* Ligne réseaux sociaux retirée — maintenant en widget flottant côté page */}

          {/* Compteur et filtres */}
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2 pt-2 border-t border-slate-700">
            <p className="text-slate-300 text-sm">
              <span className="text-lg font-bold text-white">{filteredVehicles.length}</span> véhicule(s) disponible(s)
            </p>

            {/* Barre de filtres par type */}
            <div className="flex gap-2 flex-wrap">
              <button
              onClick={() => setTypeFilter('tous')}
              data-testid="filter-tous"
              className={`flex-1 sm:flex-none px-3 py-1.5 rounded-md font-medium text-sm transition-all ${
                typeFilter === 'tous'
                  ? 'bg-red-600 text-white shadow'
                  : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
              }`}
            >
              <span className="mr-1 text-xs">🚗🚛</span>
              Tous ({vehicles.length})
            </button>
            <button
              onClick={() => setTypeFilter('voiture')}
              data-testid="filter-voiture"
              className={`flex-1 sm:flex-none px-3 py-1.5 rounded-md font-medium text-sm transition-all ${
                typeFilter === 'voiture'
                  ? 'bg-red-600 text-white shadow'
                  : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
              }`}
            >
              <span className="mr-1 text-xs">🚗</span>
              Voitures ({voitures.length})
            </button>
            <button
              onClick={() => setTypeFilter('utilitaire')}
              data-testid="filter-utilitaire"
              className={`flex-1 sm:flex-none px-3 py-1.5 rounded-md font-medium text-sm transition-all ${
                typeFilter === 'utilitaire'
                  ? 'bg-red-600 text-white shadow'
                  : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
              }`}
            >
              <span className="mr-1 text-xs">🚛</span>
              Utilitaires ({utilitaires.length})
            </button>
          </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {filteredVehicles.length === 0 ? (
          <div className="bg-white rounded-lg border border-slate-200 shadow-sm p-12 text-center">
            <p className="text-slate-500 text-lg">
              Aucun {typeFilter === 'voiture' ? 'voiture' : typeFilter === 'utilitaire' ? 'utilitaire' : 'véhicule'} disponible pour le moment
            </p>
            <p className="text-slate-400 text-sm mt-2">Revenez bientôt pour découvrir nos nouvelles offres</p>
          </div>
        ) : (
          <div className="space-y-12">
            {/* Section Voitures */}
            {displayedVoitures.length > 0 && (
              <div>
                <div className="flex items-center gap-3 mb-6">
                  <div className="flex items-center gap-2">
                    <div className="w-12 h-12 bg-slate-900 rounded-lg flex items-center justify-center">
                      <span className="text-white text-2xl">🚗</span>
                    </div>
                    <h2 className="text-3xl font-bold text-slate-900" style={{fontFamily: 'Manrope'}}>
                      Voitures
                    </h2>
                  </div>
                  <span className="px-4 py-2 bg-slate-100 text-slate-700 rounded-full text-sm font-semibold">
                    {displayedVoitures.length} disponible{displayedVoitures.length > 1 ? 's' : ''}
                  </span>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                  {displayedVoitures.map(vehicle => renderVehicleCard(vehicle))}
                </div>
              </div>
            )}

            {/* Section Utilitaires */}
            {displayedUtilitaires.length > 0 && (
              <div>
                <div className="flex items-center gap-3 mb-6">
                  <div className="flex items-center gap-2">
                    <div className="w-12 h-12 bg-blue-600 rounded-lg flex items-center justify-center">
                      <span className="text-white text-2xl">🚛</span>
                    </div>
                    <h2 className="text-3xl font-bold text-slate-900" style={{fontFamily: 'Manrope'}}>
                      Utilitaires
                    </h2>
                  </div>
                  <span className="px-4 py-2 bg-blue-100 text-blue-700 rounded-full text-sm font-semibold">
                    {displayedUtilitaires.length} disponible{displayedUtilitaires.length > 1 ? 's' : ''}
                  </span>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                  {displayedUtilitaires.map(vehicle => renderVehicleCard(vehicle))}
                </div>
              </div>
            )}
          </div>
        )}
      </main>

      {/* Contact Footer */}
      <footer className="bg-gradient-to-r from-slate-900 via-slate-800 to-slate-900 border-t-4 border-red-600 mt-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="text-center">
            <img 
              src="https://customer-assets.emergentagent.com/job_auto-flip-tracker-1/artifacts/mote16qp_26609.jpg" 
              alt="Groupe Auto Prestige" 
              className="h-20 mx-auto mb-6 object-contain"
            />
            <h3 className="text-2xl font-semibold text-white mb-4" style={{fontFamily: 'Manrope'}}>
              Intéressé par un véhicule ?
            </h3>
            <p className="text-slate-300 mb-8 text-lg">Contactez-nous pour plus d'informations</p>
            <div className="flex justify-center gap-4 flex-wrap">
              <a
                href="tel:0644859768"
                className="flex items-center gap-2 px-6 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-all shadow-lg font-semibold"
              >
                <Phone size={18} />
                <span>06 44 85 97 68</span>
              </a>
              <a
                href="tel:0789213027"
                className="flex items-center gap-2 px-6 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-all shadow-lg font-semibold"
              >
                <Phone size={18} />
                <span>07 89 21 30 27</span>
              </a>
              <a
                href="mailto:lyes_has@hotmail.com"
                className="flex items-center gap-2 px-6 py-3 bg-white text-slate-900 rounded-lg hover:bg-slate-100 transition-all shadow-lg font-semibold"
              >
                <Mail size={18} />
                <span>Email</span>
              </a>
            </div>

            <p className="text-slate-500 text-sm mt-8">© 2026 Groupe Auto Prestige - Tous droits réservés</p>
          </div>
        </div>
      </footer>

      {/* Vehicle Detail Modal */}
      {selectedVehicle && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
          onClick={closeModal}
        >
          <div
            className="bg-white rounded-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
            data-testid="vehicle-detail-modal"
          >
            <div className="sticky top-0 bg-white border-b border-slate-200 px-6 py-4 flex justify-between items-center z-10">
              <h2 className="text-2xl font-bold text-slate-900" style={{fontFamily: 'Manrope'}}>
                {selectedVehicle.marque} {selectedVehicle.modele}
              </h2>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => shareVehicleNative(selectedVehicle)}
                  data-testid="share-vehicle-btn"
                  title="Partager cette fiche"
                  aria-label="Partager cette fiche véhicule"
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-red-600 text-white rounded-md hover:bg-red-700 transition-all shadow text-sm font-medium"
                >
                  {copiedVehicle ? <Check size={16} /> : <Share2 size={16} />}
                  <span className="hidden sm:inline">{copiedVehicle ? 'Copié !' : 'Partager'}</span>
                </button>
                <button
                  onClick={closeModal}
                  data-testid="close-modal-btn"
                  className="p-2 hover:bg-slate-100 rounded-full transition-colors"
                >
                  <X size={24} />
                </button>
              </div>
            </div>

            <div className="p-6">
              {/* Images */}
              {selectedVehicle.photos && selectedVehicle.photos.length > 0 ? (
                <div className="mb-6">
                  <div
                    className="relative bg-slate-200 rounded-lg overflow-hidden mb-4 group cursor-zoom-in"
                    onClick={() => setLightboxOpen(true)}
                    data-testid="vehicle-main-image-wrapper"
                  >
                    <img
                      src={selectedVehicle.photos[selectedImage]}
                      alt="Vehicle"
                      className="w-full h-96 object-cover pointer-events-none"
                    />
                    {/* Overlay zoom indicator */}
                    <div className="pointer-events-none absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors flex items-center justify-center">
                      <div className="opacity-0 group-hover:opacity-100 transition-opacity bg-white/90 text-slate-900 px-3 py-1.5 rounded-full text-sm font-medium flex items-center gap-2 shadow-lg">
                        <ZoomIn size={16} />
                        Cliquer pour agrandir
                      </div>
                    </div>
                    {/* Compteur photos */}
                    {selectedVehicle.photos.length > 1 && (
                      <div className="pointer-events-none absolute bottom-3 right-3 bg-black/60 text-white text-xs font-medium px-2.5 py-1 rounded-full">
                        {selectedImage + 1} / {selectedVehicle.photos.length}
                      </div>
                    )}
                    {/* Flèches navigation sur l'image principale (desktop hover uniquement) */}
                    {selectedVehicle.photos.length > 1 && (
                      <>
                        <button
                          onClick={(e) => { e.stopPropagation(); prevPhoto(); }}
                          className="absolute left-2 top-1/2 -translate-y-1/2 bg-white/80 hover:bg-white text-slate-900 p-2 rounded-full shadow-lg transition-opacity opacity-0 pointer-events-none group-hover:opacity-100 group-hover:pointer-events-auto"
                          aria-label="Photo précédente"
                        >
                          <ChevronLeft size={20} />
                        </button>
                        <button
                          onClick={(e) => { e.stopPropagation(); nextPhoto(); }}
                          className="absolute right-2 top-1/2 -translate-y-1/2 bg-white/80 hover:bg-white text-slate-900 p-2 rounded-full shadow-lg transition-opacity opacity-0 pointer-events-none group-hover:opacity-100 group-hover:pointer-events-auto"
                          aria-label="Photo suivante"
                        >
                          <ChevronRight size={20} />
                        </button>
                      </>
                    )}
                  </div>
                  {selectedVehicle.photos.length > 1 && (
                    <div className="grid grid-cols-4 gap-3">
                      {selectedVehicle.photos.map((photo, index) => (
                        <div
                          key={index}
                          onClick={() => setSelectedImage(index)}
                          className={`cursor-pointer rounded-lg overflow-hidden border-2 transition-all ${
                            selectedImage === index ? 'border-slate-900 scale-105' : 'border-slate-200 hover:border-slate-400'
                          }`}
                        >
                          <img src={photo} alt={`Thumbnail ${index + 1}`} className="w-full h-24 object-cover" />
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <div className="bg-slate-200 rounded-lg h-96 flex items-center justify-center text-slate-400 mb-6">
                  Aucune photo disponible
                </div>
              )}

              {/* Details Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-slate-50 rounded-lg p-6">
                  <h3 className="text-lg font-semibold text-slate-900 mb-4" style={{fontFamily: 'Manrope'}}>
                    Caractéristiques
                  </h3>
                  <div className="space-y-3">
                    <div className="flex justify-between">
                      <span className="text-slate-600">Type</span>
                      <span className="text-slate-900">
                        {selectedVehicle.type_vehicule === 'utilitaire' ? 'Utilitaire' : 'Voiture'}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-600">Année</span>
                      <span className="text-slate-900">{selectedVehicle.annee}</span>
                    </div>
                    {selectedVehicle.couleur && (
                      <div className="flex justify-between">
                        <span className="text-slate-600">Couleur</span>
                        <span className="text-slate-900">{selectedVehicle.couleur}</span>
                      </div>
                    )}
                    {selectedVehicle.kilometrage && (
                      <div className="flex justify-between">
                        <span className="text-slate-600">Kilométrage</span>
                        <span className="font-mono text-slate-900">
                          {selectedVehicle.kilometrage.toLocaleString('fr-FR')} km
                        </span>
                      </div>
                    )}
                  </div>
                </div>

                <div className="bg-green-50 rounded-lg p-6">
                  <h3 className="text-lg font-semibold text-slate-900 mb-4" style={{fontFamily: 'Manrope'}}>
                    Prix
                  </h3>
                  {selectedVehicle.prix_vente ? (
                    <>
                      <div className="text-4xl font-bold text-green-600 font-mono">
                        {selectedVehicle.prix_vente.toLocaleString('fr-FR')} €
                        <span className="text-base font-semibold text-slate-500 ml-2">TTC</span>
                      </div>
                      {selectedVehicle.tva_applicable && (
                        <p className="text-sm text-slate-600 font-mono mt-1">
                          soit {Math.round(selectedVehicle.prix_vente / 1.20).toLocaleString('fr-FR')} € HT
                          <span className="text-xs text-slate-400 ml-1">(TVA 20%)</span>
                        </p>
                      )}
                    </>
                  ) : (
                    <p className="text-slate-600">Prix sur demande</p>
                  )}
                  <div className="mt-6 pt-6 border-t border-green-200">
                    <p className="text-sm text-slate-600 mb-3">Intéressé ? Contactez-nous</p>
                    <div className="flex flex-col gap-2">
                      <a
                        href="tel:0644859768"
                        className="flex items-center justify-center gap-2 px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors text-sm"
                      >
                        <Phone size={16} />
                        <span>06 44 85 97 68</span>
                      </a>
                      <a
                        href="tel:0789213027"
                        className="flex items-center justify-center gap-2 px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors text-sm"
                      >
                        <Phone size={16} />
                        <span>07 89 21 30 27</span>
                      </a>
                      <a
                        href="mailto:lyes_has@hotmail.com"
                        className="flex items-center justify-center gap-2 px-4 py-2 bg-white text-green-600 border border-green-600 rounded-md hover:bg-green-50 transition-colors text-sm"
                      >
                        <Mail size={16} />
                        <span>Email</span>
                      </a>
                    </div>
                  </div>
                </div>
              </div>

              {/* Description */}
              {selectedVehicle.description && (
                <div className="mt-6 bg-slate-50 rounded-lg p-6">
                  <h3 className="text-lg font-semibold text-slate-900 mb-3" style={{fontFamily: 'Manrope'}}>
                    Description
                  </h3>
                  <p className="text-slate-700 leading-relaxed">{selectedVehicle.description}</p>
                </div>
              )}
            </div>
          </div>

          {/* Lightbox plein écran */}
          {lightboxOpen && selectedVehicle.photos && selectedVehicle.photos.length > 0 && (
            <div
              className="fixed inset-0 bg-black/95 z-[60] flex items-center justify-center"
              onClick={() => setLightboxOpen(false)}
              data-testid="vehicle-lightbox"
            >
              {/* Bouton fermer */}
              <button
                onClick={(e) => { e.stopPropagation(); setLightboxOpen(false); }}
                data-testid="lightbox-close"
                className="absolute top-4 right-4 z-10 p-3 bg-white/10 hover:bg-white/20 text-white rounded-full transition-colors"
                aria-label="Fermer"
              >
                <X size={24} />
              </button>

              {/* Compteur */}
              {selectedVehicle.photos.length > 1 && (
                <div className="absolute top-4 left-1/2 -translate-x-1/2 z-10 bg-white/10 text-white text-sm font-medium px-4 py-2 rounded-full">
                  {selectedImage + 1} / {selectedVehicle.photos.length}
                </div>
              )}

              {/* Image principale plein écran */}
              <div
                className="relative w-full h-full flex items-center justify-center p-4 sm:p-12"
                onClick={(e) => e.stopPropagation()}
                onTouchStart={handleTouchStart}
                onTouchEnd={handleTouchEnd}
              >
                <img
                  src={selectedVehicle.photos[selectedImage]}
                  alt={`Photo ${selectedImage + 1}`}
                  className="max-w-full max-h-full object-contain select-none"
                  data-testid="lightbox-image"
                  draggable={false}
                />
              </div>

              {/* Flèches navigation */}
              {selectedVehicle.photos.length > 1 && (
                <>
                  <button
                    onClick={(e) => { e.stopPropagation(); prevPhoto(); }}
                    data-testid="lightbox-prev"
                    className="absolute left-2 sm:left-6 top-1/2 -translate-y-1/2 z-10 p-3 bg-white/10 hover:bg-white/20 text-white rounded-full transition-colors"
                    aria-label="Photo précédente"
                  >
                    <ChevronLeft size={28} />
                  </button>
                  <button
                    onClick={(e) => { e.stopPropagation(); nextPhoto(); }}
                    data-testid="lightbox-next"
                    className="absolute right-2 sm:right-6 top-1/2 -translate-y-1/2 z-10 p-3 bg-white/10 hover:bg-white/20 text-white rounded-full transition-colors"
                    aria-label="Photo suivante"
                  >
                    <ChevronRight size={28} />
                  </button>
                </>
              )}

              {/* Bandeau thumbnails en bas */}
              {selectedVehicle.photos.length > 1 && (
                <div
                  className="absolute bottom-4 left-1/2 -translate-x-1/2 z-10 flex gap-2 px-3 py-2 bg-white/5 rounded-lg max-w-[90vw] overflow-x-auto"
                  onClick={(e) => e.stopPropagation()}
                >
                  {selectedVehicle.photos.map((photo, idx) => (
                    <img
                      key={idx}
                      src={photo}
                      alt={`Miniature ${idx + 1}`}
                      onClick={() => setSelectedImage(idx)}
                      className={`h-14 w-20 object-cover rounded cursor-pointer transition-all flex-shrink-0 ${
                        selectedImage === idx ? 'ring-2 ring-white opacity-100' : 'opacity-60 hover:opacity-100'
                      }`}
                    />
                  ))}
                </div>
              )}

              {/* Hint clavier (desktop only) */}
              <div className="hidden sm:block absolute bottom-24 left-1/2 -translate-x-1/2 text-white/60 text-xs">
                ← → pour naviguer · Esc pour fermer
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default PublicCatalogue;
